import { useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Input, Modal, useToast } from '../../../design-system';
import { fmtDate } from '../../pages/OrgAdminRenewals';
import '../../styles/Renewals.css';

// Record a renewal: the old period is archived and the dates roll forward.
//
// Deliberately NOT the same thing as editing the expiry date. Editing corrects
// a mistake; renewing says "a new certificate was issued", and the previous
// number and validity have to survive that — a practice must be able to answer
// "what was the licence number in FY24-25?".
function RenewalModal({ record, onClose, onRenewed }) {
  const toast = useToast();
  // Default the new period to start where the old one ended and run a year —
  // the overwhelmingly common case for an annual licence.
  const startsIso = (record.validTo ? new Date(record.validTo) : new Date()).toISOString().slice(0, 10);
  const plusYear = new Date(record.validTo || Date.now());
  plusYear.setFullYear(plusYear.getFullYear() + 1);

  const [validFrom, setValidFrom] = useState(startsIso);
  const [validTo, setValidTo] = useState(plusYear.toISOString().slice(0, 10));
  const [number, setNumber] = useState(record.number || '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/api/expirables/${record.id}/renew`, { validFrom, validTo, number, note });
      toast.success('Renewal recorded');
      onRenewed?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not record the renewal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen onClose={onClose}
      title={`Renew — ${record.typeLabel}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving}>Record renewal</Button>
        </>
      }
    >
      <div className="rnw-form">
        <div className="rnw-was">
          <span className="rnw-form-label">Currently on record</span>
          <p>
            <b>{record.clientName}</b> · {record.typeLabel}
            {record.number && <> · no. {record.number}</>}
          </p>
          <p className="rnw-muted">
            Valid {fmtDate(record.validFrom)} → {fmtDate(record.validTo)}
          </p>
        </div>

        <div className="rnw-form-row">
          <Input label="New period starts" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          <Input label="New expiry" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
        </div>
        <Input label="New certificate number (if it changed)" value={number} onChange={(e) => setNumber(e.target.value)} />
        <Input label="Note (optional)" placeholder="e.g. renewed at the municipal office" value={note} onChange={(e) => setNote(e.target.value)} />

        <p className="rnw-sub">
          The period above is archived to this record's history before the dates move, so the old
          number and validity are never lost. Reminders reset, so the next cycle starts clean.
        </p>

        {record.renewalCount > 0 && (
          <>
            <span className="rnw-form-label">Previous periods</span>
            <div className="rnw-hist">
              {record.history.map((h, i) => (
                <div className="rnw-hist-row" key={i}>
                  <span>{fmtDate(h.validFrom)} → {fmtDate(h.validTo)}</span>
                  <span className="rnw-muted">{h.number || '—'}</span>
                  <span className="rnw-muted">{h.renewedByName || ''}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default RenewalModal;
